/* eslint-disable */
import React from 'react'
import ReactMarkdown from 'react-markdown'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { isMobile } from 'react-device-detect'
import Sites from '@sites/index'
import { CookiesBanner, CookiesModal } from '@telus/koodo-ui-kit'
import FloatingCookieManager from '@apps/components/FloatingCookieManager'
import { getCookie } from '@apps/utils/common'
import { convertContentfulMarkdown } from '@apps/utils/contentfulMarkdown'
import { getCookieBodyContent, extractTextFromHTML } from '@apps/utils/contentful'
import { useCookieConsent, ACKNOWLEDGE_COOKIE_NAME } from '@/_engine/hooks/useCookieConsent'
import useLocale from '@/_engine/hooks/useLocale'
import { setPageAnalytics } from '@/apps/utils/Analytics'
import { ANALYTICS_COOKIE_CONSTANTS, ANALYTICS_EVENT_TYPES } from '@/apps/utils/constants'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

/**
 * CookieManagementSystem - Centralized Cookie Management System
 * 
 * This component manages all cookie-related functionality in one place:
 * - Cookie Consent Banner
 * - Cookie Modal (cookie settings modal)
 * - Floating Cookie Manager (floating cookie management button)
 * 
 * Usage:
 * Add once to top-level route component (e.g., ActivationRoutes).
 * <CookieManagementSystem />
 */
const CookieManagementSystem = () => {
  const { lang, prov } = useLocale()
  const { userType } = useSelector((state) => state.common)
  const location = useLocation()
  const tagManagerInfo = useTagManagerInfo()

  // Manage all cookie consent logic through the useCookieConsent hook
  const {
    showCookieModal,
    setShowCookieModal,
    showCookieConsentBanner,
    cookieConsentObject,
    cookieControllers,
    setCookieControllers,
    handleManageCookies,
    handleAcceptAllCookies,
    handleCloseBannerClick,
    handleCloseModalClick,
    handleToggle,
    onModalSubmit,
    isRegionActive
  } = useCookieConsent({
    showBanner: true,
    language: lang,
    province: prov,
    userType: userType
  })
  
  // Only render for consumer users
  if (userType !== Sites.consumer) {
    return null
  }

  // eslint-disable-next-line camelcase
  const pushClickAnalytics = ({ click_text, event_type, eventCategory, event_click = ANALYTICS_EVENT_TYPES.CLICK, consentObject } = {}) => {
    if (userType !== 'consumer') {
      return
    }
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      setPageAnalytics(location, 'click', prov, lang, {
        ...tagManagerInfo,
        click_text,
        eventCategory,
        consentObject
      }, event_type, event_click)
    )
  }

  const buildConsentObject = () => {
    const enhancedCookieObject = {}
    cookieControllers.forEach((item) => {
      item?.target?.forEach((cookieKey) =>
        item?.granted
          ? (enhancedCookieObject[cookieKey] = 'granted')
          : (enhancedCookieObject[cookieKey] = 'denied')
      )
    })
    return enhancedCookieObject
  }

  const pushCookieAnalyticsEvent = (id, eventCategory) => {
    pushClickAnalytics({
      click_text: id,
      event_type: `${id} Clicked`,
      eventCategory,
      consentObject: buildConsentObject()
    })
  }

  const handleCookieAnalyticsEvent = ({ id, source } = {}) => {
    if (!id) return

    pushCookieAnalyticsEvent(
      id,
      source === 'modal'
        ? ANALYTICS_COOKIE_CONSTANTS.COOKIE_MODAL
        : ANALYTICS_COOKIE_CONSTANTS.COOKIE_FOOTER
    )
  }

  const getAnalyticsText = (children) => {
    const extractTextFromReactNode = (node) => {
      if (typeof node === 'string' || typeof node === 'number') {
        return String(node)
      }
      if (Array.isArray(node)) {
        return node.map(extractTextFromReactNode).join('')
      }
      if (React.isValidElement(node)) {
        return extractTextFromReactNode(node.props?.children)
      }
      return ''
    }
    return extractTextFromReactNode(children)
  }

  const renderCookieMarkdown = (
    rawText,
    eventCategory,
    linkClassName = '',
    emitAnalyticsEvent,
    analyticsSource
  ) => {
    const isHTML = rawText && rawText.includes('<')
    let processedContent = isHTML ? extractTextFromHTML(rawText) : rawText || ''
    processedContent = convertContentfulMarkdown(processedContent)
    const emitEvent = emitAnalyticsEvent || handleCookieAnalyticsEvent

    return (
      <ReactMarkdown
        components={{
          p: ({ children }) => <span>{children}</span>,
          a: ({ href, title, children }) => {
            const id =
              typeof title === 'string' && title.startsWith('id:')
                ? title.slice(3)
                : undefined

            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', fontWeight: 'bold' }}
                data-id={id}
                onClick={() => {
                  if (id) {
                    emitEvent({
                      source: analyticsSource || (
                        eventCategory === ANALYTICS_COOKIE_CONSTANTS.COOKIE_MODAL ? 'modal' : 'banner'
                      ),
                      action: 'linkClick',
                      id,
                      href,
                      text: getAnalyticsText(children)
                    })
                  }
                }}
                className={linkClassName}
              >
                {children}
              </a>
            )
          },
          strong: ({ children }) => <strong>{children}</strong>,
          em: ({ children }) => <em>{children}</em>,
          del: ({ children }) => <del>{children}</del>
        }}
      >
        {processedContent}
      </ReactMarkdown>
    )
  }

  const bannerModalContent = {
    ...cookieConsentObject,
    text: getCookieBodyContent(cookieConsentObject, isMobile)
  }
  
  // Function to load current cookie state before opening the cookie modal
  const handleOpenCookieModal = () => {
    // Read current cookie value and update controller state
    const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
    if (cookieValue) {
      try {
        const consent = JSON.parse(cookieValue)
        
        // Update controllers based on cookie values
        setCookieControllers(prev => prev.map(category => {
          const isGranted = category.required || 
                          (category.target && category.target.length > 0 && 
                           category.target.every(key => consent[key] === 'granted'))
          
          return {
            ...category,
            granted: isGranted
          }
        }))
        pushClickAnalytics({ click_text: ANALYTICS_COOKIE_CONSTANTS.MANAGE_COOKIES, event_type: `${ANALYTICS_COOKIE_CONSTANTS.MANAGE_COOKIES} Clicked`, eventCategory: ANALYTICS_COOKIE_CONSTANTS.MANAGE_COOKIES_LINK, consentObject: consent })
      } catch (e) {
        console.error('Failed to load cookie consent:', e)
      }
    }
    setShowCookieModal(true)
  }
  
  return (
    <>
      {/* Cookie Consent Banner - Cookie consent banner displayed at the bottom of the page */}
      {showCookieConsentBanner && (
        <>
          <CookiesBanner
            modalContent={bannerModalContent}
            handleManageCookies={handleManageCookies}
            handleAcceptAllCookies={handleAcceptAllCookies}
            handleCloseClick={handleCloseBannerClick}
            closeAriaLabel={cookieConsentObject?.shortText?.closeCookieBannerAriaLabel || 'Close cookie banner'}
            isMobile={isMobile}
            contentMaxWidth={1200}
            onAnalyticsEvent={() => {
              // TODO: Handle analytics events if needed
            }}
            renderText={(rawText) =>
              renderCookieMarkdown(
                rawText,
                ANALYTICS_COOKIE_CONSTANTS.COOKIE_FOOTER,
                'font-h4',
                undefined,
                'banner'
              )
            }
          />
        </>
      )}
      
      {/* Cookie Modal - Cookie setting modal that opens via banner or floating manager */}
      {showCookieModal && (
        <>
          <CookiesModal
            modalContent={cookieConsentObject}
            handleCloseClick={handleCloseModalClick}
            cookieControllers={cookieControllers}
            handleToggle={handleToggle}
            onSubmit={onModalSubmit}
            closeAriaLabel={cookieConsentObject?.shortText?.closeModalAriaLabel || 'Close modal'}
            modalWidth="min(100%, 576px)"
            modalMaxWidth={576}
            onAnalyticsEvent={() => {
              // TODO: Handle analytics events if needed
            }}
            renderDescription={(rawText) =>
              renderCookieMarkdown(
                rawText,
                ANALYTICS_COOKIE_CONSTANTS.COOKIE_MODAL,
                '',
                undefined,
                'modal'
              )
            }
            renderControllerContent={(item) =>
              renderCookieMarkdown(
                item?.content || item?.description || '',
                ANALYTICS_COOKIE_CONSTANTS.COOKIE_MODAL,
                '',
                undefined,
                'modal'
              )
            }
          />
        </>
      )}
      
      {/* Floating Cookie Manager - Bottom right floating button (only show if region is active) */}
      {isRegionActive() && (
        <FloatingCookieManager 
          onManageCookies={handleOpenCookieModal}
          showCookieModal={showCookieModal}
          showCookieConsentBanner={showCookieConsentBanner}
          cookieControllers={cookieControllers}
          setCookieControllers={setCookieControllers}
        />
      )}
    </>
  )
}

export default CookieManagementSystem
